import { Injectable } from '@angular/core';

import { FirestorePathsService } from './firestorePaths.service';
import { DateService } from './date.service';
import { FirestoreService } from '../lib/firestore.service';

import type {
    TSummary,
    // TSummaryCampaignDoc,
    TSummarySmsDailyValues,
    TSummaryType,
    // TSummaryTripDaily,
    // TSummaryTicketDoc,
    // TSummaryTripDoc,
    TDailyValueUids,
    TTimestamp,
    TSummaryTripDailyValues,
    TUser,
    // TDailyValueDoc
} from '@shared/models';
import type { Observable } from 'rxjs';

@Injectable({
    'providedIn': 'root'
})
export class SummaryService {
    constructor(private firestoreService: FirestoreService, private firestorePathsService: FirestorePathsService,
        private dateService: DateService) {
    }

    // private getSummaryReference() {
    //     const collection = this.firestorePathsService.getSummariesCol();
    //     const summaryDoc = collection.doc('summary');
    //     const ref = summaryDoc.ref;
    //     return ref;
    // }

    public getObservable(): Observable<TSummary> {
        const path = this.firestorePathsService.getSummaryPath();
        const observable = this.firestoreService.getObservable(path) as Observable<TSummary>;
        return observable;
    }

    public async get(): Promise<TSummary> {
        const path = this.firestorePathsService.getSummaryPath();
        const summary = await this.firestoreService.get(path) as TSummary;
        return summary;
    }

    public async getAll(): Promise<TSummary[]> {
        const path = this.firestorePathsService.getSummaryPath();
        return this.firestoreService.getAll(path) as Promise<TSummary[]>;
    }

    // public async getByUid(uid: string): Promise<unknown> {
    public async getByUid(uid: TSummaryType): Promise<unknown> {
        const path = this.firestorePathsService.getSummaryPath();
        // return this.firestoreService.getByUid(collection, uid) as Promise<TSummary>;
        return this.firestoreService.getByUid(path, uid);
    }

    public async getOverallSummary(): Promise<TSummary> {
        const overallSummary = await this.getByUid('overallSummary') as TSummary;
        return overallSummary;
    }

    public async getByAccountNumber(accountNumber: string): Promise<TSummary> {
        const path = this.firestorePathsService.getSummaryByAccountNumberPath(accountNumber);
        return this.firestoreService.get(path) as Promise<TSummary>;
    }

    public async setDocument(obj: TSummary, uid: string): Promise<TSummary> {
        obj.uid = uid;
        return this.set(obj) as Promise<TSummary>;
    }

    public async set(summary: TSummary) {
        const path = this.firestorePathsService.getSummaryPath();
        return this.firestoreService.set(path, summary);
    }

    public async update(summary: TSummary): Promise<TSummary> {
        const path = this.firestorePathsService.getSummaryPath();
        const retval = await this.firestoreService.update(path, summary);
        return (retval as TSummary);
    }

    public async getSummaryDocument(documentName: string): Promise<TSummary> {
        const path = this.firestorePathsService.getSummaryPath();
        const summary = await this.firestoreService.get(path) as TSummary;   // we might need to append docmentName to this path
        return summary;
    }

    // public async getOverallSummaryDocument(): Promise<TSummary> {
    //     const path = this.firestorePathsService.getSummaryPath();
    //     const summary = await this.firestoreService.get(path) as TSummary;   // we might need to append docmentName to this path
    //     return summary;
    //     // const summaryDoc = collection.doc(documentName);
    //     // const ref = summaryDoc.ref;
    //     // return ref;
    // }

    public async getDailyCountsByDocument(documentName: TSummaryType): Promise<TSummary> {
        const ref = this.getSummaryDocument(documentName);
        return ref as Promise<TSummary>;    // this is wrong. Debug it.
        // return this.firestoreService.get(ref) as Promise<TSummary>;
    }

    public async getTicketsByDateRange(startTimestamp: TTimestamp, endTimestamp: TTimestamp)
        : Promise<any> {
        // We are only interested in those trips that were completed in the selected time frame.
        const doc = await this.getByUid('ticketsSummary') as any;
        if (doc) {
            const dailyValues = this.filterDailyValuesByTimePeriod(doc.dailyValues, startTimestamp, endTimestamp);

            const uids: string[] = [];
            dailyValues.forEach((daily: any) => {
                daily.uids.forEach((uid: string) => {
                    uids.push(uid);
                });
            });
            return { 'uids': uids, 'dailyValues': dailyValues };
        }

        return null;
    }

    public async getTripsByDateRange(startTimestamp: TTimestamp, endTimestamp: TTimestamp) : Promise<TDailyValueUids> {
        const uids: string[] = [];
        const dailyValues: any[] = [];
        // TODO: There are other methods that need to follow this style of coding, like getCampaignUidsByDateRange.
        //       I will refactor them later.

        // We are only interested in those trips that were completed in the selected time frame.
        // const doc = await this.getByUid('tripsSummary') as TSummaryTripDoc;
        const doc = await this.getByUid('tripsSummary') as any;
        if (doc) {
            const values = this.filterDailyValuesByTimePeriod(doc.dailyValues, startTimestamp, endTimestamp);
            const dailyValues = values as TSummaryTripDailyValues[];

            dailyValues.forEach((daily) => { // Specify the type of 'daily' parameter
                daily.uids.forEach((uid: string) => {
                    uids.push(uid);
                });
            });
            // return {'uids': uids, 'dailyValues': dailyValues };
        }

        return { 'uids': uids, 'dailyValues': dailyValues };
    }

    public async getCampaignUidsByDateRange(startTimestamp: TTimestamp, endTimestamp: TTimestamp)
        : Promise<TDailyValueUids | null> {
        // We are only interested in those campaigns that were completed in the selected time frame.
        // const doc = await this.getByUid('campaignsSummary') as TSummaryCampaignDoc;
        const doc = await this.getByUid('campaignsSummary') as any;
        if (doc) {
            const dailyValues = this.filterDailyValuesByTimePeriod(doc.dailyValues, startTimestamp, endTimestamp);

            const uids: string[] = [];
            dailyValues.forEach((daily: any) => {
                daily.campaignUids.forEach((uid: string) => {
                    uids.push(uid);
                });
            });
            return { 'uids': uids, 'dailyValues': dailyValues };
        }

        return null;
    }

    private filterDailyValuesByTimePeriod(dailyValues: any[], startTimestamp: TTimestamp, endTimestamp: TTimestamp) {
        dailyValues = dailyValues || [];

        const selectedDailyValues = dailyValues.filter((daily) => {
            const timestamp = this.dateService.toTimestamp(daily.date);
            const withinTimePeriod = ((timestamp.seconds >= startTimestamp.seconds) &&
                (timestamp.seconds <= endTimestamp.seconds));
            return withinTimePeriod;
        });

        return selectedDailyValues;
    }

    public async getSmsByDateRange(startTimestamp: TTimestamp, endTimestamp: TTimestamp): Promise<TDailyValueUids | null> {
        // We are only interested in those trips that were completed in the selected time frame.
        // const doc = await this.getByUid('smsSummary') as TSummaryTicketDoc;
        const doc = await this.getByUid('smsSummary') as any;
        if (doc) {
            const dailyValues = this.filterDailyValuesByTimePeriod(doc.dailyValues, startTimestamp, endTimestamp);

            const uids: string[] = [];
            dailyValues.forEach((daily) => {
                daily.uids.forEach((uid: string) => {
                    uids.push(uid);
                });
            });
            return { 'uids': uids, 'dailyValues': dailyValues };
        }

        return null;

        // let smsDailyValues: TSummarySmsDailyValues[] = [];

        // // We are only interested in those trips that were completed in the selected time frame.
        // const doc = await this.getByUid('smsSummary') as TSummarySmsDoc;
        // if (doc) {
        //     smsDailyValues = doc.dailyValues;
        //     // let dailyValues = <TDailyValueDoc>doc;  // .dailyValues;

        //     const selectedSms = smsDailyValues.filter(daily => {
        //         const timestamp = this.dateService.toTimestamp(daily.date);
        //         const withinTimePeriod = ((timestamp.seconds >= startTimestamp.seconds) &&
        //                                   (timestamp.seconds <= endTimestamp.seconds));
        //         return withinTimePeriod;
        //     });
        //     return selectedSms;
        // }

        // return null;
    }

    // public extractTicketUidsFromTrips(tripsDailyValues: TSummaryTripDailyValues[]) {
    //     const uids: string[] = [];

    //     tripsDailyValues.forEach(daily => {
    //         const date = daily.date;
    //         const count = daily.count;
    //         const trips = daily.trips;
    //         console.log(date, count, JSON.stringify(trips));
    //         const trip = trips[0];
    //         console.log(typeof trip);

    //         console.log(trip);
    //         // const uid = trip.tripUid;
    //         // const ticketUids  = trip.ticketUids;

    //         trips.forEach(trip => {
    //             trip.ticketUids.forEach(uid => {
    //                 uids.push(uid);
    //             });
    //         });


    //         daily.trips.forEach(trip => {
    //             trip.ticketUids.forEach(uid => {
    //                 uids.push(uid);
    //             });
    //         });
    //     });

    //     return uids;
    // }

    public extractUidsFromSms(smsDailyValues: TSummarySmsDailyValues[]) {
        const allUids: string[] = [];

        smsDailyValues.forEach((daily) => {
            allUids.push(...daily.smsUids);
        });

        return allUids;
    }
}
